import { RA_SHIP_GROUPS } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Drawer,
  Fieldset,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from '../../shell-context.js';
import { usePmsErrorText } from '../pms/EquipmentDrawer.js';
import type { VesselParticulars, VesselParticularsInput } from '../pms/types.js';
import { Fact, Facts } from '../settings/SettingsLayout.js';
import {
  OWNER_FIELDS,
  type ParticularsFormValues,
  particularsFormOf,
  particularsFormSchema,
  toParticularsInput,
} from './particulars-form.js';

const FIELDS = ['vesselType', 'builtYear', 'flag', 'classSociety', 'classNo', 'callSign'] as const;

/** Gemide salt okunur gemi özellikleri (ofis yönetir; ADR 0029 §2): etiketli satırlar. */
export function VesselParticularsFacts({ particulars }: { particulars: VesselParticulars }) {
  const { t } = useTranslation();
  return (
    <Facts>
      {FIELDS.map((field) => (
        <Fact key={field} label={t(`pms.particulars.${field}`)}>
          {particulars[field] ?? '—'}
        </Fact>
      ))}
    </Facts>
  );
}

/**
 * Uygulanan doküman şablonu (ADR 0037 §4; ADR 0038 §5): gemi eklenirken seçilir, sonradan yalnız
 * platform (Felixmate) değiştirir; çekmecede salt okunurdur.
 */
export function VesselDocTemplate({
  template,
}: {
  template: { id?: string; name: string | null; version: number } | null;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`pms.particulars.${key}`, options ?? {});
  return (
    <Facts>
      <Fact label={k('docTemplate')}>
        {template
          ? k('docTemplateValue', { name: template.name ?? '—', version: template.version })
          : k('docTemplateNone')}
        <Text size="xs" c="dimmed">
          {k('docTemplateHint')}
        </Text>
      </Fact>
    </Facts>
  );
}

export interface VesselParticularsDrawerProps {
  opened: boolean;
  vesselId: string | null;
  load: (vesselId: string) => Promise<VesselParticulars>;
  save: (
    vesselId: string,
    input: VesselParticularsInput & { rowVersion: number },
  ) => Promise<VesselParticulars>;
  /** Bugünün yılı: yapım yılı gelecekte olamaz. */
  year: number;
  /**
   * Verilirse çekmece salt okunur (ADR 0151 Ek): alanlar kapalı, Save yerine bu neden ve Close.
   * Sunucu düzenleme yetkisi yok dediğinde (ofiste filo kapsamlı `settings.edit`).
   */
  readOnlyReason?: string | null;
  onClose: () => void;
  onSaved?: (particulars: VesselParticulars) => void;
}

/**
 * Çekmecenin alt satırı: düzenlemede Cancel ve Save; salt okunurda Save'in yerinde kilit simgesi
 * ve neden, yanında Close (ADR 0151 §1 kalıbı: düğme yerine hangi yetkinin gerektiği).
 */
export function ParticularsFooter({
  readOnlyReason,
  busy,
  ready,
  onClose,
}: {
  readOnlyReason: string | null;
  busy: boolean;
  /** Kayıt yüklendi: Save açılır. */
  ready: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.particulars.${key}`);
  if (readOnlyReason !== null) {
    return (
      <Group justify="space-between" gap="sm" wrap="nowrap">
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconLock size={14} aria-hidden />
          <Text size="xs" c="dimmed">
            {readOnlyReason}
          </Text>
        </Group>
        <Button variant="default" onClick={onClose}>
          {k('close')}
        </Button>
      </Group>
    );
  }
  return (
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={onClose}>
        {k('cancel')}
      </Button>
      <Button type="submit" loading={busy} disabled={!ready}>
        {k('save')}
      </Button>
    </Group>
  );
}

/**
 * Fleet ▸ Vessels'te gemi özellikleri çekmecesi (plan M7 Task 6c): Vessel Type, Built, Flag, Class,
 * Class No, Call Sign. Şirket verisi; ofiste `settings.edit` ile yazılır (sunucu denetler), gemiye
 * iner ve Equipment List üst kartında görünür. React Hook Form + Zod (domain şeması).
 */
export function VesselParticularsDrawer({
  opened,
  vesselId,
  load,
  save,
  year,
  readOnlyReason = null,
  onClose,
  onSaved,
}: VesselParticularsDrawerProps) {
  const readOnly = readOnlyReason !== null;
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.particulars.${key}`);
  const errorText = usePmsErrorText();
  const [current, setCurrent] = useState<VesselParticulars | null>(null);
  const [error, setError] = useState<unknown>(null);
  const form = useForm<ParticularsFormValues>({
    resolver: zodResolver(particularsFormSchema(year)),
    defaultValues: {
      vesselType: '',
      builtYear: '',
      flag: '',
      classSociety: '',
      classNo: '',
      callSign: '',
      raGroup: '',
      grossTonnage: '',
      ismCompany: '',
      ownerSpeedLadenKn: '',
      ownerSpeedBallastKn: '',
      ownerConsumptionLadenMt: '',
      ownerConsumptionBallastMt: '',
    },
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened || !vesselId) return;
    let live = true;
    setCurrent(null);
    setError(null);
    load(vesselId)
      .then((value) => {
        if (!live) return;
        setCurrent(value);
        form.reset(particularsFormOf(value));
      })
      .catch((failure: unknown) => {
        if (live) setError(failure);
      });
    return () => {
      live = false;
    };
  }, [opened, vesselId, load, form]);

  const fieldError = (name: keyof ParticularsFormValues) => {
    const message = errors[name]?.message;
    return message
      ? t(`pms.fieldErrors.${message}`, { defaultValue: t('pms.errors.VALIDATION_FAILED') })
      : undefined;
  };

  const submit = form.handleSubmit(async (values) => {
    if (!current || !vesselId || readOnly) return;
    setError(null);
    try {
      const saved = await save(vesselId, {
        rowVersion: current.rowVersion,
        ...toParticularsInput(values),
      });
      form.reset(particularsFormOf(saved));
      onSaved?.(saved);
    } catch (failure) {
      setError(failure);
    }
  });

  const text = (
    name: 'vesselType' | 'flag' | 'classSociety' | 'classNo' | 'callSign' | 'ismCompany',
  ) => <TextInput label={k(name)} {...form.register(name)} error={fieldError(name)} />;
  // Owner's reference (ADR 0084 Ek 2): charter'sız seferin hız ve tüketimi; boş alan yok
  const owner = (name: (typeof OWNER_FIELDS)[keyof typeof OWNER_FIELDS], unit: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={k(name)}
          allowNegative={false}
          decimalScale={2}
          rightSection={<span>{unit}</span>}
          rightSectionWidth={64}
          value={field.value}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          onBlur={field.onBlur}
          error={fieldError(name)}
        />
      )}
    />
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={460}
      title={current ? `${k('title')} · ${current.name}` : k('title')}
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          {current && current.docTemplate !== undefined ? (
            <VesselDocTemplate template={current.docTemplate} />
          ) : null}
          {current ? (
            // Salt okunurda alanlar kapalı (ADR 0151 Ek): fieldset bütün girişleri kapatır. Yalnız
            // davranış için: tarayıcının fieldset kenar boşluğu (2 px) da sıfır, yerleşim değişmez.
            <Fieldset variant="unstyled" p={0} m={0} disabled={readOnly}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {text('vesselType')}
                <Controller
                  control={form.control}
                  name="builtYear"
                  render={({ field }) => (
                    <NumberInput
                      label={k('builtYear')}
                      allowDecimal={false}
                      allowNegative={false}
                      thousandSeparator={false}
                      value={field.value}
                      onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                      onBlur={field.onBlur}
                      error={fieldError('builtYear')}
                    />
                  )}
                />
                {text('flag')}
                {text('classSociety')}
                {text('classNo')}
                {text('callSign')}
                {/* GT ve ISM şirketi (ADR 0117 §8): MRV/ETS uygunluk önerisinin girdisi */}
                <TextInput
                  label={k('grossTonnage')}
                  inputMode="decimal"
                  {...form.register('grossTonnage')}
                  error={fieldError('grossTonnage')}
                />
                {text('ismCompany')}
                {/* Risk ana verisinin gemi grubu (ADR 0086 §4): gemide önerilen faaliyetler */}
                <Controller
                  control={form.control}
                  name="raGroup"
                  render={({ field }) => (
                    <Select
                      label={k('raGroup')}
                      description={k('raGroupHint')}
                      value={field.value}
                      allowDeselect={false}
                      data={[
                        { value: '', label: t('riskMaster.groups.ALL') },
                        ...RA_SHIP_GROUPS.map((g) => ({
                          value: g,
                          label: t(`riskMaster.groups.${g}`),
                        })),
                      ]}
                      onChange={(value) => field.onChange(value ?? '')}
                      onBlur={field.onBlur}
                      error={fieldError('raGroup')}
                    />
                  )}
                />
              </SimpleGrid>
            </Fieldset>
          ) : null}
          {current ? (
            <Fieldset legend={k('ownerReference')} p="sm" disabled={readOnly}>
              <Text size="sm" c="dimmed" mb="xs">
                {k('ownerReferenceHint')}
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {owner(OWNER_FIELDS.speedLadenKn, 'kn')}
                {owner(OWNER_FIELDS.speedBallastKn, 'kn')}
                {owner(OWNER_FIELDS.consumptionLadenMt, 'MT/day')}
                {owner(OWNER_FIELDS.consumptionBallastMt, 'MT/day')}
              </SimpleGrid>
            </Fieldset>
          ) : error ? null : (
            <Skeleton height={160} />
          )}
          <ParticularsFooter
            readOnlyReason={readOnlyReason}
            busy={isSubmitting}
            ready={current !== null}
            onClose={onClose}
          />
        </Stack>
      </form>
    </Drawer>
  );
}
